import { Device, Channel, UnitChannel, SourceType } from './ptou';

type Location = {
  id: number;
  name: string;
  address: string;
  backlogIssueKey: string;
  constructionStatus: LocationConstructionStatus;
  units: Unit[];
  projects: Project[];
};

type RegistrationStatus = 'unregistered' | 'registering' | 'registered';

type Unit = {
  id: number;
  locationId: Location['id'];
  no: number;
  name: string;
  sourceType: SourceType;
  simId: string;
  readonly simSessionStatus: boolean | null;
  datapoints: Datapoint[];
  //sourceType='ptou' の時のみ存在
  device?: Device;
  channels?: UnitChannel[];
  createdAt: string;
  updatedAt: string;
  /** ユニットとデバイスの最終同期時刻 (ISO 文字列、未同期は null) */
  lastSyncedAt: string | null;
  /** 登録ステータス (unregistered=SIM・Serial 無 / registering=Serial 有 SIM 無 / registered=SIM 有) */
  registrationStatus: RegistrationStatus;
};

type PulserateUnit = 'm3' | 'L' | 'kWh' | '°C';

type Datapoint = {
  id: number;
  name: string;
  address: string;
  unitId: Unit['id'];
  terminalId: number;
  pulserate: number;
  pulserateUnit: PulserateUnit;
  interval: number;
  calcMethod: boolean;
  isAvailable: boolean;
  deviceNo: number;
  // PTOU
  channelId: Channel['id'] | null;
};

// value / pulserate は精度劣化を避けるため API 上は文字列で返る（高精度小数）。
// プロット等で number として扱う場合は呼び出し側で Number(...) する。
type Data = {
  datapointId: Datapoint['id'];
  startAt: string;
  endAt: string;
  value: string;
  pulserate: string;
  pulserateUnit: PulserateUnit;
  // 使用量 (value × pulserate) の演算結果。value / pulserate と同様、精度劣化を避けるため文字列で返る。
  usage: string;
  createdAt: string;
  updatedAt: string;
};

type InterpolatedData = {
  datapointId: Datapoint['id'];
  startAt: Date;
  endAt: Date;
  value: number;
};

type Project = {
  id: number;
  name: string;
  isAvailable: boolean;
  postcode: string;
  address: string;
  latitude: number;
  longitude: number;
  backlogIssueKey: string;
  isEnableSummaryScore: boolean;
  isEnableSummaryDetail: boolean;
  isEnableSummaryTotal: boolean;
  isKenesIntegrationAvailable: boolean;
  locations: Location[];
  projectGroups: Pick<ProjectGroup, 'id' | 'name'>[];
  utilities: Utility[];
};

type ProjectOverview = Pick<Project, 'id' | 'name' | 'isAvailable'>;

type ProjectLocation = {
  projectId: Project['id'];
  locationId: Location['id'];
  location: Location;
};

// ReadProjectGroup https://www.notion.so/picoada/ReadProjectGroup-1788a7af74674dff892c8de1a02c965f
type ProjectGroup = {
  id: number;
  name: string;
  detail: string;
  deleteProtection: boolean;
  projects: ProjectOverview[];
  clientUsers: ProjectGroupClientUser[];
};

type ProjectGroupClientUser = Pick<
  ClientUser,
  'id' | 'isAvailable' | 'username' | 'email' | 'name'
>;

type Color = 'red' | 'blue' | 'green' | 'yellow';

type NodeDatapoint = {
  nodeId: Node['id'];
  datapointId: Datapoint['id'];
  coefficient: number;
  datapoint: Datapoint;
};

type Node = {
  id: number;
  name: string;
  projectId: Project['id'];
  order: number;
  parentId: Node['id'] | null;
  depth: number;
  v1GraphNodeId: number;
  energyType: EnergyType;
  relativeOrder: number;
  relativeOrderPath: number[];
  ancestorIds: Node['id'][];
  datapoints: (Pick<
    Datapoint,
    'id' | 'name' | 'address' | 'terminalId' | 'deviceNo' | 'isAvailable'
  > & { coefficient: number })[];
};

type TreeNode = Node & {
  children: TreeNode[];
};

type TreeNodeWithHash = Omit<TreeNode, 'children'> & {
  hash: string;
  children: TreeNodeWithHash[];
};

type ContractTerm = {
  id: number;
  projectId: Project['id'];
  startAt: string;
  endAt: string;
};

type Utility = {
  id: number;
  projectId: Project['id'];
  name: string;
  order: number;
  energyType: EnergyType;
  isEnableSummary: boolean;
  nodeId: Node['id'] | null;
};

type ReductionTargetData = {
  utilityId: Utility['id'];
  contractTermId: ContractTerm['id'];
  values: MonthValue[] | null;
  projectId: Project['id'];
};

type MonthValue = {
  month: number;
  value: number | null;
};

type StandardData = {
  utilityId: Utility['id'];
  contractTermId: ContractTerm['id'];
  values: MonthValue[] | null;
  projectId: Project['id'];
};

type Unitcost = {
  utilityId: Utility['id'];
  contractTermId: ContractTerm['id'];
  value?: number | null;
  projectId: Project['id'];
};

type UnitcostWithKey = Unitcost & {
  key: string;
};

type StandardDataWithKey = StandardData & {
  key: string;
};

type ReductionTargetDataWithKey = ReductionTargetData & {
  key: string;
};

const RoleEnum = {
  superadmin: 'superadmin',
  general: 'general',
  signal: 'signal',
  kenes: 'kenes',
  data: 'data',
} as const;

type Role = keyof typeof RoleEnum;

/**
 * アドミンユーザのリソース制限。ロール権限とは別軸で操作可能なリソースを絞る。
 * 値は API のワイヤ文字列に合わせる（キーのみ camelCase）。
 */
const AdminResourceFilterStatusEnum = {
  unrestricted: 'unrestricted', // 制限なし
  kenesOnly: 'kenes_only', // KENES関連リソースのみ
  constructionOnly: 'construction_only', // 工事関連リソースのみ
} as const;

type AdminResourceFilterStatus =
  (typeof AdminResourceFilterStatusEnum)[keyof typeof AdminResourceFilterStatusEnum];

/** リソース制限の表示用ラベル（一覧カラム等で使用）。 */
const AdminResourceFilterStatusLabel: Record<AdminResourceFilterStatus, string> = {
  unrestricted: '制限なし',
  kenes_only: 'KENESのみ',
  construction_only: '工事中のみ',
};

/** ロケーションの工事ステータス（将来的に KANNA ステータスと連携予定）。 */
const LocationConstructionStatusEnum = {
  notStarted: 'not_started', // 未工事
  inProgress: 'in_progress', // 工事中
  completed: 'completed', // 工事完了
} as const;

type LocationConstructionStatus =
  (typeof LocationConstructionStatusEnum)[keyof typeof LocationConstructionStatusEnum];

/** 工事ステータスの表示用ラベル（一覧カラム等で使用）。 */
const LocationConstructionStatusLabel: Record<LocationConstructionStatus, string> = {
  not_started: '未工事',
  in_progress: '工事中',
  completed: '工事完了',
};

type AdminUser = {
  id: number;
  username: string;
  isAvailable: boolean;
  email: string;
  name: string;
  company: string;
  department: string;
  position: string;
  role: Role;
  resourceFilterStatus: AdminResourceFilterStatus;
};

type ClientUser = {
  id: number;
  username: string;
  isAvailable: boolean;
  email: string;
  name: string;
  company: string;
  department: string;
  position: string;
  role: Role;
  projects: ClientUserProject[];
  projectGroups: ClientUserProjectGroup[];
};

type ClientUserProject = Pick<Project, 'id' | 'name' | 'isAvailable'>;
type ClientUserProjectGroup = Pick<ProjectGroup, 'id' | 'name'>;

type Status = {
  status: string;
  date: Date;
};

type EnergyType = {
  id: number;
  name: string;
  unit: string;
  color: Color;
};

type EnergyTypes = {
  energyTypes: EnergyType[];
};

export type {
  Location,
  Unit,
  Datapoint,
  Data,
  InterpolatedData,
  Project,
  ProjectOverview,
  ProjectGroup,
  ProjectGroupClientUser,
  Node,
  TreeNodeWithHash,
  TreeNode,
  ContractTerm,
  Utility,
  ReductionTargetData,
  StandardData,
  UnitcostWithKey,
  StandardDataWithKey,
  ReductionTargetDataWithKey,
  MonthValue,
  Unitcost,
  AdminUser,
  ClientUser,
  ClientUserProject,
  ClientUserProjectGroup,
  Status,
  Color,
  NodeDatapoint,
  PulserateUnit,
  ProjectLocation,
  Role,
  AdminResourceFilterStatus,
  LocationConstructionStatus,
  EnergyType,
  EnergyTypes,
  RegistrationStatus,
};
export {
  RoleEnum,
  AdminResourceFilterStatusEnum,
  AdminResourceFilterStatusLabel,
  LocationConstructionStatusEnum,
  LocationConstructionStatusLabel,
};
